import { Component, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, inject } from '@angular/core';
import { NgForm, NgModel } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import Swal from 'sweetalert2';
import { ResidencyBasis } from '../../../models/enums/residency-basis.enum';
import { ResidencyAddress } from '../../../models/residency-address.model';
import { RealEstateOwned } from '../../../models/mortgage.model';
import { AddressComponent } from '../../address/address.component';
import { ConfigurableFieldHostComponent } from '../../configurable-field-host.component';
import { Address } from '../../../models/address-model';
import { v4 as uuidv4 } from 'uuid';
import {
  computeDurationThrough,
  hasDuration,
  isDurationLocked,
  parseMaskedDate,
  sameDuration,
  toMaskedDate,
} from '../../../utils/residency-duration';

@Component({
    selector: 'address-history2',
    templateUrl: 'address-history2.component.html',
    standalone: false
})
export class AddressHistory2Component extends ConfigurableFieldHostComponent implements OnInit, OnChanges {

  @Input() residencyAddress: ResidencyAddress | undefined;

  @Input() reo: RealEstateOwned | undefined;

  /**
   * LLS-569: only the current-address instance shows Move-In Date. Previous
   * addresses would need a move-out date to make sense of it (out of scope).
   */
  @Input() showMoveInDate: boolean = false;

  @Output() durationAtAddressChanged = new EventEmitter<number>();

  @Output() addressChanged = new EventEmitter<Address>();

  @Output() ownershipStatusChanged: EventEmitter<ResidencyBasis> = new EventEmitter<ResidencyBasis>();

  @Output() dateAcquiredChanged = new EventEmitter<void>();

  @ViewChild('addressComponent') addressComponent: AddressComponent | undefined;

  @ViewChild('addressRelatedInfoForm') addressRelatedInfoForm: NgForm | undefined;

  uniqueId: string;

  protected isMobile: boolean = false;

  /**
   * The last Move-In Date the component committed as the source of the current durations - set by
   * the init/re-bind sync, by an accepted overwrite, and by a clear. While it parses to a date, the
   * current durations were DERIVED from it (not hand-entered), so a later blur re-derives silently
   * instead of confirming. See onMoveInDateBlur.
   */
  private committedMoveInDate: string | undefined;

  private readonly _transloco = inject(TranslocoService);

  constructor() {
    super();
    if (!this.residencyAddress) {
      this.residencyAddress = new ResidencyAddress("PresentAddress");
    }
    this.uniqueId = uuidv4();
  }

  ngOnInit() {
    this.configureBasedOnScreenSize(window.innerWidth);
    if (!this.residencyAddress.residencyBasis) {
      this.residencyAddress.residencyBasis = null;
    }
    this.syncFromMoveInDate();
  }

  ngOnChanges(changes: SimpleChanges) {
    // The parent (borrower-address-history) re-runs initializeAddresses() on changes['borrower'],
    // which can re-bind [residencyAddress] to a different object after this component has already
    // initialized (e.g. switching which borrower's address history is shown). Re-sync so the
    // newly-bound address's durations reflect its own Move-In Date rather than the previous
    // binding's.
    if (changes['residencyAddress'] && !changes['residencyAddress'].firstChange) {
      this.syncFromMoveInDate();
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.configureBasedOnScreenSize(window.innerWidth);
  }

  /**
   * Converts a persisted Move-In Date to the mask and re-derives Years/Months THROUGH TODAY from
   * it, without any confirm - the date has already won. Runs on init and on every re-bind so a
   * returning borrower never sees a duration frozen at the day the date was typed. Hidden instances
   * (showMoveInDate === false) leave the value untouched, as before LLS-569.
   */
  private syncFromMoveInDate(): void {
    if (!this.showMoveInDate || !this.residencyAddress) return;

    // The Api returns ISO; the mask needs MM/DD/YYYY.
    this.residencyAddress.moveInDate = toMaskedDate(this.residencyAddress.moveInDate);
    const movedIn = parseMaskedDate(this.residencyAddress.moveInDate);
    if (movedIn) {
      const duration = computeDurationThrough(movedIn, new Date());
      const changed = !sameDuration(this.residencyAddress, duration);
      this.residencyAddress.durationYears = duration.years;
      this.residencyAddress.durationMonths = duration.months;
      if (changed) {
        // Deferred so a parent binding changed by this sync (e.g. during ngOnChanges) does not
        // throw ExpressionChangedAfterItHasBeenChecked - same pattern as onMonthsOrYearsBlurred.
        setTimeout(() => this.durationAtAddressChanged.emit(duration.months + 12 * duration.years));
      }
    }

    this.committedMoveInDate = this.residencyAddress.moveInDate;
  }

  /** Years/Months are derived from Move-In Date and read-only while one is present. */
  get durationLocked(): boolean {
    return this.showMoveInDate && !!this.residencyAddress && isDurationLocked(this.residencyAddress);
  }

  /**
   * ConfigurableFieldHost.isRequired() falls back to true for an unregistered key.
   * Move-In Date ships not-required and several hosts pass no config at all
   * (co-borrower address history), so resolve it with an explicit false fallback.
   */
  moveInDateRequired = (): boolean => {
    return this.fieldConfig?.['residencyAddress.moveInDate']?.isRequired ?? false;
  }

  onAddressChanged = (address: Address) => {
    this.addressChanged.emit(address);
  }

  onOwnershipStatusChanged = () => {
    this.ownershipStatusChanged.emit(this.residencyAddress.residencyBasis as ResidencyBasis);
  }

  onMonthsOrYearsBlurred = (e: any) => {
    setTimeout(() => {
      const focusedElement = document.activeElement;
      if (focusedElement) {
        if (focusedElement.id !== "monthsAtAddress" && focusedElement.id !== "yearsAtAddress") {
          const years = this.residencyAddress!.durationYears ? this.residencyAddress!.durationYears : 0;
          const months = this.residencyAddress!.durationMonths ? this.residencyAddress!.durationMonths : 0;
          this.durationAtAddressChanged.emit(months + 12 * years);
        }
      }
    });
  }

  /**
   * Fires on blur of the Move-In Date input. Blur (not ngModelChange) so the Swal cannot fire
   * mid-typing. A cleared date unlocks Years/Months and leaves them as they were.
   *
   * The overwrite confirm only ever fires against a HAND-ENTERED duration. If the current
   * durations were themselves derived from a previously committed Move-In Date (committedMoveInDate
   * parses), the new date has already won by the same rule the init sync applies, so it is applied
   * silently - confirming the replacement of a derived number is noise. Otherwise the durations are
   * hand-entered or imported, so a differing duration is confirmed as before.
   *
   * Net effect: declining ("No") discards only the newly typed date and falls back to whatever was
   * last committed (undefined when nothing was ever committed) - it can never erase a persisted
   * Move-In Date.
   */
  onMoveInDateBlur = async (moveInDateCtl: NgModel) => {
    if (!moveInDateCtl?.dirty) return;
    if (!this.residencyAddress) return;
    if (!this.residencyAddress.moveInDate) {
      this.residencyAddress.moveInDate = undefined;
      this.committedMoveInDate = undefined;
      return;
    }

    const movedIn = parseMaskedDate(this.residencyAddress.moveInDate);
    if (!movedIn) return;

    const duration = computeDurationThrough(movedIn, new Date());

    if (parseMaskedDate(this.committedMoveInDate)) {
      this.applyDuration(duration);
      this.committedMoveInDate = this.residencyAddress.moveInDate;
      return;
    }

    if (sameDuration(this.residencyAddress, duration)) {
      this.committedMoveInDate = this.residencyAddress.moveInDate;
      return;
    }

    if (hasDuration(this.residencyAddress)) {
      const confirmed = await this.confirmOverwrite('wizard.addressHistory.overwriteDurationFromMoveInConfirm', duration);
      if (!confirmed) {
        this.residencyAddress.moveInDate = this.committedMoveInDate;
        moveInDateCtl.control.markAsPristine();
        return;
      }
    }

    this.applyDuration(duration);
    this.committedMoveInDate = this.residencyAddress.moveInDate;
  }

  /**
   * Fires on blur of the Date Acquired input. Doing the work on blur (rather than
   * ngModelChange) avoids triggering the Swal mid-typing once the masked year
   * completes a valid date. The dirty check prevents the Swal from firing when
   * the borrower simply tabs through a pre-populated field. Date Acquired still
   * saves and still emits for refinance, but it only drives the duration while
   * no Move-In Date is present (LLS-569 precedence).
   */
  onDateAcquiredBlur = async (dateAcquiredCtl: NgModel) => {
    if (!dateAcquiredCtl?.dirty) return;
    if (!this.reo || !this.residencyAddress) return;
    if (!this.reo.dateAcquired) {
      this.reo.dateAcquired = undefined;
    }
    this.dateAcquiredChanged.emit();

    if (this.durationLocked) return;

    const acquired = parseMaskedDate(this.reo.dateAcquired);
    if (!acquired) return;

    const duration = computeDurationThrough(acquired, new Date());
    if (sameDuration(this.residencyAddress, duration)) return;

    if (hasDuration(this.residencyAddress)) {
      const confirmed = await this.confirmOverwrite('wizard.addressHistory.overwriteDurationConfirm', duration);
      if (!confirmed) return;
    }

    this.applyDuration(duration);
  }

  validate = () => {
    const isAddressValid = this.addressComponent?.validate();
    this.addressRelatedInfoForm?.form.markAllAsTouched();
    if (isAddressValid && this.addressRelatedInfoForm?.form.valid) {
      return true;
    }
    return false;
  }

  private applyDuration = ({ years, months }: { years: number; months: number }) => {
    this.residencyAddress!.durationYears = years;
    this.residencyAddress!.durationMonths = months;
    this.durationAtAddressChanged.emit(months + 12 * years);
  }

  private confirmOverwrite = async (messageKey: string, { years, months }: { years: number; months: number }): Promise<boolean> => {
    const result = await Swal.fire({
      title: this._transloco.translate('common.areYouSure'),
      text: this._transloco.translate(messageKey, { years, months }),
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: this._transloco.translate('common.yes'),
      cancelButtonText: this._transloco.translate('common.no'),
    });
    return result.isConfirmed;
  }

  private configureBasedOnScreenSize(windowWidth: number) {
    this.isMobile = windowWidth < 768;
  }
}
